import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Hero from '../sections/Hero';
import SplitSection from '../sections/SplitSection';
import VideoSection from '../sections/VideoSection';
import HorizontalVideoSection from '../sections/HorizontalVideoSection';
import SymbolStory from '../sections/SymbolStory';
import CollectionGrid from '../sections/CollectionGrid';
import Footer from '../sections/Footer';

const VIDEO = {
  one: '/images/video/0209.MP4',
  two: '/images/video/' + encodeURI('0210 (1)(2)-1 2.MP4'),
  three: '/images/video/' + encodeURI('576d29b6-78d7-47d0-8ba8-80c9b8fae9c5 2.mov'),
  four: '/images/video/' + encodeURI('7e483c8b-4c30-46b5-a300-28fc9b9c5b79 4.mov'),
  five: '/images/video/' + encodeURI('Vertical_9_16_TikTok_styl...ikTok_Reels_ready_content.mov'),
  horizontal: '/images/video/horizontal.mp4',
};

// All image paths from public/images (root and subfolders)
const IMG = {
  lookbookBurgundy: '/images/VYERA_Lookbook_Burgundy_Set_Correcto.png',
  lookbookBlack: '/images/VYERA_Lookbook_Black_Set_Correcto.png',
  editorial1: '/images/VYERA_Editorial_1.png',
  dropLookbook: '/images/VYERA_DROP01_Lookbook_02_2.png',
  lookbookCreamTee: '/images/VYERA_Lookbook_Cream_Tee_Correcto.png',
  casualLifestyle: '/images/VYERA_Casual_Lifestyle.png',
  lifestyleBurgundy: '/images/VYERA_Lifestyle_Burgundy_Corregido.png',
  editorial3: '/images/VYERA_Editorial_3.png',
  powLeanLifestyle: '/images/' + encodeURI('VYERA POW LEAN - Lifestyle FX3 Corregido.png'),
  // Shoes — section hero from public/images/shoes/, strip from ads
  shoesSection: '/images/shoes/VYERA_Zapatos_Lifestyle_Hombre_Black.jpg',
  shoesAdLifestyle: '/images/ads/Ad_VYERA_Lifestyle.png',
  shoesAdHero: '/images/ads/Ad_VYERA_Hero_Banner.png',
  shoesAdFullCollection: '/images/ads/Ad_VYERA_Full_Collection.png',
  shoesAdSidebar: '/images/ads/Ad_VYERA_Sidebar.png',
} as const;

export default function Home() {
  return (
    <div className="relative">
      <Navigation />
      <main className="relative">
        {/* 1. Hero */}
        <Hero />

        {/* 2. Story — Presence over noise */}
        <SplitSection
          id="story"
          imageSrc={IMG.lookbookBurgundy}
          imageAlt="VYERA lifestyle - Presence over noise"
          headline="Presence over noise."
          body="VYERA was created for those who move with intention. Not louder. Not faster. Just consistent."
          cta="Read our story"
          zIndex={20}
        />

        <VideoSection src={VIDEO.one} headline="Movement in motion." body="One rhythm." zIndex={21} />

        {/* 3. Product — Designed for continuity */}
        <SplitSection
          imageSrc={IMG.lookbookBlack}
          imageAlt="VYERA product - Designed for continuity"
          headline="Designed for continuity."
          body="Every piece is made to move with you — from training to stillness."
          cta="Shop"
          ctaHref="/shop"
          zIndex={30}
        />

        <VideoSection src={VIDEO.two} headline="Movement in motion." body="Show up. Repeat." zIndex={31} />

        {/* 4. Lookbook — This is VYERA */}
        <SplitSection
          imageSrc={IMG.editorial1}
          imageAlt="VYERA lookbook - This is VYERA"
          headline="This is VYERA."
          body="Clean silhouettes. Refined materials. Minimal presence."
          cta="View the lookbook"
          zIndex={40}
        />

        <VideoSection src={VIDEO.three} headline="Movement in motion." body="Feel the frequency." zIndex={41} />

        {/* 5. The drop */}
        <SplitSection
          imageSrc={IMG.dropLookbook}
          imageAlt="VYERA drop lookbook"
          headline="The drop."
          body="Essential pieces. One collection."
          cta="Shop the drop"
          ctaHref="/shop"
          zIndex={45}
        />

        <VideoSection src={VIDEO.four} headline="Movement in motion." body="Built for the everyday." zIndex={46} />

        {/* 5b. Shoes — image from public/images/shoes/ */}
        <SplitSection
          imageSrc={IMG.shoesSection}
          imageAlt="VYERA shoes - lifestyle"
          headline="Shoes that move with you."
          body="Light, minimal, built for the everyday. For him and her."
          cta="Shop shoes"
          ctaHref="/shop#shoes"
          zIndex={47}
        />

        <VideoSection src={VIDEO.five} headline="Movement in motion." body="One rhythm." zIndex={48} />

        {/* 6. Symbol */}
        <SymbolStory />

        {/* 7. Collection grid — Apparel & details from public/images */}
        <CollectionGrid />

        {/* 7b. Shoes — single ad: VYERA NEW COLLECTION */}
        <section
          className="relative w-full bg-[var(--vyera-cream)] py-10 px-6"
          style={{ zIndex: 72 }}
        >
          <div className="max-w-2xl mx-auto">
            <div className="vyera-img-ad vyera-img-ad--compact mb-6">
              <img src={IMG.shoesAdFullCollection} alt="VYERA NEW COLLECTION - shoes" />
            </div>
            <div className="text-center">
              <Link to="/shop#shoes" className="vyera-cta inline-block">
                Shop shoes
              </Link>
            </div>
          </div>
        </section>

        {/* 7c. Unboxing — horizontal video from public/images/video/ */}
        <HorizontalVideoSection
          src={VIDEO.horizontal}
          headline="Unbox the experience."
          zIndex={73}
        />

        {/* 8. Supplements — POW LEAN from public/images */}
        <SplitSection
          imageSrc={IMG.powLeanLifestyle}
          imageAlt="POW LEAN - Vitamin supplement by VYERA"
          headline="POW LEAN."
          body="Rhythm for your body. Vitamins and care in one rhythm."
          cta="Shop supplements"
          ctaHref="/shop#supplements"
          zIndex={75}
        />

        {/* 9. Movement is a practice */}
        <SplitSection
          imageSrc={IMG.lookbookCreamTee}
          imageAlt="VYERA lifestyle - Movement is a practice"
          headline="Movement is a practice."
          body="Show up. Repeat. Let the work speak."
          cta="Join the list"
          zIndex={80}
        />

        {/* 10. Everyday */}
        <SplitSection
          imageSrc={IMG.casualLifestyle}
          imageAlt="VYERA casual lifestyle"
          headline="Built for the everyday."
          body="From studio to street. One rhythm."
          cta="Explore"
          ctaHref="/shop"
          zIndex={85}
        />

        {/* 11. Editorial */}
        <SplitSection
          imageSrc={IMG.editorial3}
          imageAlt="VYERA editorial"
          headline="Refined."
          body="Less distraction. More intention."
          cta="Shop"
          ctaHref="/shop"
          zIndex={90}
        />

        <div id="contact">
          <Footer />
        </div>
      </main>
    </div>
  );
}
